/**
 * AI explain store — fetch + cache `{summary, details}` blurbs from
 * `/api/ai/explain`.
 *
 * Caching strategy: keyed by `${kind}:${stableHash(context)}`. The hash is
 * a tiny non-cryptographic fingerprint built from JSON.stringify so the
 * same diagnosis row / alert / failed run reuses the cached explanation
 * across re-renders and even page navigations within a session.
 *
 * Why a store (vs. useState in the component): the same setting may be
 * shown twice (e.g. the row + a summary panel both reference VLAN 51),
 * and the cache lets both consumers share one fetch.
 *
 * Errors: 503 from the shim → store records `error` and the component
 * shows a one-line "AI explanations are unavailable" message instead of
 * the panel. Anything else (404, 500) is surfaced verbatim.
 */
import { create } from "zustand";
import { post as apiPost, ApiError } from "@/api/client";

export type AiExplainKind =
  | "diagnosis-setting"
  | "alert"
  | "site-health-alert"
  | "site-wireless-failed-connection"
  | "site-dashboard-summary"
  | "monitor-dashboard-summary"
  | "diagnosis-summary"
  | "workflow-run-failure";

export interface AiExplanation {
  summary: string;
  details: string;
  model: string;
  /** Round-trip latency reported by the shim (incl. Anthropic + network). */
  elapsedMs: number;
}

/** A single cached entry plus its loading/error state. */
interface CacheEntry {
  status: "loading" | "ready" | "error";
  data?: AiExplanation;
  error?: string;
}

interface State {
  /** Keyed by `${kind}:${hash}`. */
  byKey: Record<string, CacheEntry>;

  /**
   * Fetch (or return cached) explanation. Idempotent — calling twice with
   * the same args while the first is still loading is a no-op.
   * Pass `force: true` to bypass the cache (e.g. a "Re-explain" button).
   */
  load: (
    kind: AiExplainKind,
    context: Record<string, unknown>,
    opts?: { force?: boolean; },
  ) => Promise<AiExplanation | null>;

  /** Build the cache key the way `load()` does — exposed so components
   *  can read entries directly without re-fetching. */
  keyFor: (kind: AiExplainKind, context: Record<string, unknown>) => string;
}

/**
 * djb2-style fingerprint of a stringified context. Not cryptographic; just
 * needs to be stable + cheap. Collisions are theoretically possible but
 * vanishingly rare for the small payloads we send (one diagnosis row, one
 * alert), and a collision would just cause one user to see another's
 * cached explanation — annoying, not security-critical.
 */
function stableHash(obj: unknown): string {
  const s = JSON.stringify(obj);
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  // Coerce to unsigned 32-bit + base36 for a short readable string.
  return (h >>> 0).toString(36);
}

const keyFor = (kind: AiExplainKind, context: Record<string, unknown>): string =>
  `${kind}:${stableHash(context)}`;

export const useAiExplain = create<State>((set, get) => ({
  byKey: {},
  keyFor,

  load: async (kind, context, opts) => {
    const key = keyFor(kind, context);
    const existing = get().byKey[key];
    if (!opts?.force && existing) {
      // Cached or in-flight — don't re-fire.
      if (existing.status === "ready") return existing.data ?? null;
      if (existing.status === "loading") return null;
    }

    set((s) => ({ byKey: { ...s.byKey, [key]: { status: "loading" } } }));
    try {
      const raw = await apiPost<unknown>("/ai/explain", { kind, context });
      // Trust-but-verify: the shim's response shape is locked, but a 503
      // from a downstream Anthropic outage would have come through as
      // ApiError above. Anything else here means we have an object with
      // {summary, details, model, elapsedMs}.
      const data = raw as AiExplanation;
      set((s) => ({
        byKey: { ...s.byKey, [key]: { status: "ready", data } },
      }));
      return data;
    } catch (err) {
      // 503 = AI backend unreachable (e.g. Anthropic down). Surface as a
      // friendly hint rather than a generic error.
      const message =
        err instanceof ApiError && err.status === 503
          ? "AI explanations are temporarily unavailable"
          : err instanceof ApiError
            ? `API error ${err.status}`
            : err instanceof Error
              ? err.message
              : "unknown error";
      set((s) => ({
        byKey: { ...s.byKey, [key]: { status: "error", error: message } },
      }));
      return null;
    }
  },
}));
